import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import { Close, Save, EditNote } from "@mui/icons-material";
import { useGetAllSectionType } from "../../../../src/hooks/panelEditor/useGetAllSectionType";
import { usePutSectionType } from "../../../../src/hooks/panelEditor/usePutSectionType";
import { toastAlert } from "../../../utils/AlertContext";

const SectionEditModal = ({ isOpen, onClose, onSave, section, token }) => {
  const [sectionName, setSectionName] = useState("");
  const [selectedSectionType, setSelectedSectionType] = useState("");
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);

  const { AllSectionTypes, loading, error } = useGetAllSectionType(token);
  const { updateSectionType, loading: updateLoading } = usePutSectionType(
    token,
    onSave,
  );

  useEffect(() => {
    if (section) {
      setSectionName(section.name || "");
      setSelectedSectionType(section.type || "");
    }
  }, [section]);

  useEffect(() => {
    const isDisabled =
      !sectionName.trim() || !selectedSectionType || updateLoading;
    setIsSaveButtonDisabled(isDisabled);
  }, [sectionName, selectedSectionType, updateLoading]);

  const handleSave = async () => {
    if (!isSaveButtonDisabled && section?.id) {
      const success = await updateSectionType(section.id, selectedSectionType);
      if (success) {
        toastAlert("Tipo de sección actualizado correctamente", "success");
        handleClose();
      }
    }
  };

  const handleClose = () => {
    setSectionName("");
    setSelectedSectionType("");
    onClose();
  };

  const currentTypeLabel = AllSectionTypes.find(
    (t) => t.value === selectedSectionType,
  )?.label;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ bgcolor: "#1565c0", px: 3, py: 2.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <EditNote sx={{ color: "white", fontSize: 22 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "white",
                fontSize: "1rem",
                letterSpacing: 0.5,
              }}
            >
              Editar tipo de sección
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            disabled
            label="Título de la sección"
            value={sectionName}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "#f9fafb",
              },
              "& .MuiInputLabel-root": { fontWeight: 500 },
            }}
          />

          <FormControl fullWidth error={!!error}>
            <InputLabel shrink sx={{ fontWeight: 500 }}>
              {currentTypeLabel
                ? `Tipo · ${currentTypeLabel}`
                : "Tipo de sección"}
            </InputLabel>
            <Select
              value={selectedSectionType}
              label={
                currentTypeLabel
                  ? `Tipo · ${currentTypeLabel}`
                  : "Tipo de sección"
              }
              onChange={(e) => setSelectedSectionType(e.target.value)}
              disabled={loading}
              displayEmpty
              notched
              sx={{
                borderRadius: "12px",
                "& .MuiSelect-select": { py: 1.75 },
              }}
            >
              {loading && (
                <MenuItem disabled value="">
                  <Typography variant="body2" color="text.secondary">
                    Cargando tipos...
                  </Typography>
                </MenuItem>
              )}
              {AllSectionTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {type.label}
                    </Typography>
                    {type.description && (
                      <Typography variant="caption" color="text.secondary">
                        {type.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 1 }}
              >
                Error al cargar tipos: {error}
              </Typography>
            )}
          </FormControl>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={updateLoading}
            fullWidth
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#d1d5db",
              color: "#374151",
              py: 1.2,
              "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveButtonDisabled}
            startIcon={<Save />}
            fullWidth
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              bgcolor: "#1976d2",
              boxShadow: "0 4px 12px rgba(25,118,210,0.35)",
              "&:hover": {
                bgcolor: "#1565c0",
                boxShadow: "0 6px 16px rgba(25,118,210,0.45)",
              },
              "&:disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e",
                boxShadow: "none",
              },
            }}
          >
            {updateLoading ? "Actualizando..." : "Actualizar"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default SectionEditModal;
